// @ts-nocheck
/*
 * Port of views/dashboard/server-management/add.js (origin/main-dev, 237 lines).
 * BYTE-IDENTICAL — every validation (data-centre, type, IPv4, name
 * allow-list, OS/CPU/RAM/hard-disk required), payload shape, toasts.
 * Chrome swap: v1 <Card> → shadcn.
 */
import { useState, useEffect } from 'react';
import { postApiCall } from '@/service/httpCall';
import { API_SERVER_MANAGEMENT_CREATE } from '@/service/url';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/utilities/constant';
import RenderServerManagementForm from '@/features/server-management/ServerManagementForm';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Loader2, Save } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function ServerManagementCreate() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [dataCenterId, setDataCenterId] = useState('');
  const [serverType, setServerType] = useState('');
  const [serverIP, setServerIP] = useState('');
  const [serverName, setServerName] = useState('');
  const [OS, setOS] = useState('');
  const [CPUCores, setCPUCores] = useState('');
  const [RAM, setRAM] = useState('');
  const [hardDisk, setHardDisk] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [countryData, setCountryData] = useState({});
  const [stateData, setStateData] = useState({});
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const value = e.target.value;
    const onlyValid = value.replace(/[^a-zA-Z0-9_]/g, '');
    if (value !== onlyValid) {
      setError('Only letters, numbers, and underscore (_) are allowed.');
    } else {
      setError('');
    }
    setServerName(onlyValid);
  };

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function validateForm() {
    let valid = true;

    if (!dataCenterId) {
      valid = false;
      toast.error('Data Center required');
    } else if (!serverType) {
      valid = false;
      toast.error('Server Type required');
    } else if (!serverIP.trim()) {
      valid = false;
      toast.error('Gateway IP required');
    } else {
      const ipRegex =
        /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
      if (!ipRegex.test(serverIP.trim())) {
        valid = false;
        toast.error('Invalid IPv4 address');
      }
    }

    if (!serverName.trim()) {
      valid = false;
      toast.error('Gateway Name required');
    } else if (!OS.trim()) {
      valid = false;
      toast.error('OS required');
    } else if (!CPUCores.trim()) {
      valid = false;
      toast.error('CPU Cores required');
    } else if (!RAM.trim()) {
      valid = false;
      toast.error('RAM required');
    } else if (!hardDisk.trim()) {
      valid = false;
      toast.error('Hard Disk required');
    }

    return valid;
  }

  function submitForm(e) {
    e.preventDefault();
    setButtonDisable(true);

    if (!validateForm()) {
      setButtonDisable(false);
      return;
    }

    const params = {
      data_center: dataCenterId,
      type: Number(serverType),
      server_ip: serverIP,
      server_name: serverName,
      os: OS,
      cpu_cores: CPUCores,
      ram: RAM,
      hard_disk: hardDisk,
    };

    postApiCall(API_SERVER_MANAGEMENT_CREATE, params)
      .then(onServerCreateSuccess)
      .catch(onServerCreateError);
  }

  function onServerCreateSuccess() {
    toast.success('Gateway created successfully');
    setTimeout(() => {
      navigate(`/${ROUTES.SERVER_MANAGEMENT.LIST}`);
    }, 1500);
  }

  function onServerCreateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    }
    setButtonDisable(false);
    toast.error(friendlyApiError(err));
  }

  return (
    <ScrollPage>
      <PageHeader
        title="New Gateway"
        description="Register a SIP gateway, media server, or PBX endpoint."
        actions={
          <Button asChild variant="outline">
            <Link to={`/${ROUTES.SERVER_MANAGEMENT.LIST}`}>
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </Button>
        }
      />
      <Card className="p-4">
        <form onSubmit={(e) => submitForm(e)}>
          <RenderServerManagementForm
                  dataCenterId={dataCenterId}
                  setDataCenterId={setDataCenterId}
                  serverType={serverType}
                  setServerType={setServerType}
                  serverIP={serverIP}
                  setServerIP={setServerIP}
                  serverName={serverName}
                  setServerName={setServerName}
                  OS={OS}
                  setOS={setOS}
                  CPUCores={CPUCores}
                  setCPUCores={setCPUCores}
                  RAM={RAM}
                  setRAM={setRAM}
                  hardDisk={hardDisk}
                  setHardDisk={setHardDisk}
                  countryData={countryData}
                  setCountryData={setCountryData}
                  stateData={stateData}
                  setStateData={setStateData}
                  handleChange={handleChange}
                  error={error}
                />

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button asChild variant="ghost">
              <Link to={`/${ROUTES.SERVER_MANAGEMENT.LIST}`}>Cancel</Link>
            </Button>
            <Button type="submit" disabled={buttonDisable}>
              {buttonDisable ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {buttonDisable ? 'Adding…' : 'Add gateway'}
            </Button>
          </div>
        </form>
      </Card>
    </ScrollPage>
  );
}
